import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getQueryWorker } from '@/engine/worker-client';
import type { DistinctValueEntry } from '@/types/query';
import {
  usePinnedFiltersStore,
  type SlotDraft,
} from '@/store/pinned-filters-store';
import { useDatasetStore } from '@/store/dataset-store';

type Props = {
  datasetId: string;
  column: string;
};

const SEARCH_DEBOUNCE_MS = 200;
const VALUE_LIMIT = 200;

/**
 * Pinned multi-select slot for text columns. Reads/writes its draft via the
 * pinned-filters store; the parent {@link PinnedFilterBar} owns the commit.
 */
export function TextSlot({ datasetId, column }: Props): JSX.Element {
  const { t } = useTranslation();
  const draft = usePinnedFiltersStore((s) => s.drafts[column]);
  const setDraft = usePinnedFiltersStore((s) => s.setDraft);
  const transform = useDatasetStore(
    (s) => s.datasets.get(datasetId)?.valueGroupings?.[column],
  );

  const [open, setOpen] = useState(false);
  const [rawSearch, setRawSearch] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState<DistinctValueEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = new Set<string>(
    draft && draft.operator === 'in' && Array.isArray(draft.value)
      ? (draft.value as Array<string | number>).map(String)
      : [],
  );

  // Debounce search input.
  useEffect(() => {
    const handle = window.setTimeout(
      () => setSearch(rawSearch),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [rawSearch]);

  // Load distinct values whenever the dropdown is open and the search changes.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const api = getQueryWorker();
    api
      .distinctValues({
        source: datasetId,
        column,
        ...(search ? { search } : {}),
        ...(transform ? { transform } : {}),
        limit: VALUE_LIMIT,
      })
      .then((res) => {
        if (cancelled) return;
        setValues(res.values);
        setHasMore(res.hasMore);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, datasetId, column, search, transform]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  function emit(nextSelected: Set<string>): void {
    const next: SlotDraft | null =
      nextSelected.size === 0
        ? null
        : { operator: 'in', value: [...nextSelected] };
    setDraft(column, next);
  }

  function toggle(v: string): void {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    emit(next);
  }

  function selectAllVisible(): void {
    const next = new Set(selected);
    for (const v of values) next.add(v.value);
    emit(next);
  }

  function clearAll(): void {
    emit(new Set());
  }

  const buttonLabel =
    selected.size === 0
      ? t('filters.pinned.all')
      : t('filters.pinned.selectedCount', { n: selected.size });

  return (
    <div ref={containerRef} className="relative flex flex-col gap-0.5">
      <label
        className="text-[10px]"
        style={{ color: 'var(--color-muted)' }}
      >
        {t('filters.pinned.selection')}
      </label>
      <div className="flex gap-1 items-stretch">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 text-xs px-2 py-1 rounded text-left truncate"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-fg)',
          }}
          title={column}
        >
          {buttonLabel}
        </button>
        <button
          type="button"
          onClick={clearAll}
          disabled={selected.size === 0}
          aria-label={t('filters.pinned.clearSelectionAria', { column })}
          className="text-xs px-1.5 rounded disabled:opacity-40"
          style={{
            border: '1px solid var(--color-border)',
            color: 'var(--color-muted)',
          }}
        >
          ×
        </button>
      </div>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 p-2 rounded-lg shadow-lg flex flex-col gap-2 min-w-[240px] max-w-[320px]"
          style={{
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
          }}
        >
          <input
            type="text"
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
            placeholder={t('filters.pinned.popoverSearch')}
            className="text-xs p-1 rounded"
            style={{ border: '1px solid var(--color-border)' }}
            autoFocus
          />
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              onClick={selectAllVisible}
              className="hover:opacity-80"
              style={{ color: 'var(--color-accent)' }}
            >
              {t('filters.pinned.popoverSelectAll')}
            </button>
            <span style={{ color: 'var(--color-muted)' }}>·</span>
            <button
              type="button"
              onClick={clearAll}
              className="hover:opacity-80"
              style={{ color: 'var(--color-accent)' }}
            >
              {t('filters.pinned.popoverClear')}
            </button>
            <span className="ml-auto" style={{ color: 'var(--color-muted)' }}>
              {t('filters.pinned.selectedCount', { n: selected.size })}
            </span>
          </div>
          <div
            className="flex flex-col gap-0.5 overflow-auto max-h-[240px] min-h-[80px] pr-1"
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: 4,
            }}
          >
            {loading && (
              <div
                className="text-xs p-2"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('filters.pinned.loading')}
              </div>
            )}
            {!loading && error && (
              <div
                className="text-xs p-2"
                style={{ color: 'var(--color-danger)' }}
              >
                {error}
              </div>
            )}
            {!loading && !error && values.length === 0 && (
              <div
                className="text-xs p-2"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('filters.pinned.noMatch')}
              </div>
            )}
            {!loading &&
              !error &&
              values.map((v) => (
                <label
                  key={v.value}
                  className="flex items-center gap-2 px-2 py-0.5 text-xs cursor-pointer hover:opacity-80"
                  style={{ color: 'var(--color-fg)' }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(v.value)}
                    onChange={() => toggle(v.value)}
                  />
                  <span className="truncate flex-1" title={v.value}>
                    {v.value}
                  </span>
                  <span
                    className="flex-shrink-0 text-[10px] tabular-nums"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {v.count.toLocaleString()}
                  </span>
                </label>
              ))}
            {!loading && !error && hasMore && (
              <div
                className="text-[10px] px-2 py-1"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('filters.pinned.moreResults')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
